"use strict";
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Placing worklogs in a day column — C12's calendar, as pure functions.      │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `main.jsx` 1223–1291. Here rather than in the screen because both halves are arithmetic with
 * exact answers — where an entry sits, and which entries collide — and a screen is the one place
 * that cannot be tested cheaply. `hard-won.md` records the shape: the isolation suite is *"the one
 * suite that cannot be replaced by looking at the screen"*, and lane packing is the one piece of
 * layout with the same property.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.CAL_STACK_FROM = exports.CAL_ROW = exports.CAL_TO = exports.CAL_FROM = void 0;
exports.parseMinutes = parseMinutes;
exports.formatMinutes = formatMinutes;
exports.snapMinutes = snapMinutes;
exports.placeDay = placeDay;
exports.packLanes = packLanes;
exports.minutesToPx = minutesToPx;
exports.columnHeightPx = columnHeightPx;
/** The calendar's window, in minutes from midnight. The mockup's `CAL_FROM` / `CAL_TO`. */
exports.CAL_FROM = 7 * 60;
exports.CAL_TO = 19 * 60;
/** Pixels per hour. The mockup's `CAL_ROW`. */
exports.CAL_ROW = 48;
/**
 * ⚠️ **Entries without a start time stack from 09:00, and that is the mockup's rule, not ours.**
 * `calDayLogs` reads `w.s` and lays the rest out from `9 * 60` in sequence. Most entries are
 * recorded after the fact as a duration and nothing else — see 0017's note on why `started_at` is
 * nullable — so the calendar has to place them *somewhere*, and a defensible fiction beats either
 * hiding them or refusing to draw the view.
 */
exports.CAL_STACK_FROM = 9 * 60;
/** `09:00` → 540. Accepts `9`, `9:30`, `9.30`, `9,30`, as the mockup's `calParseMin` does. */
function parseMinutes(value) {
    const cleaned = value.trim().replace(',', ':').replace('.', ':');
    const match = /^(\d{1,2})(?::(\d{1,2}))?$/.exec(cleaned);
    if (match === null)
        return null;
    const hours = Number(match[1]);
    const minutes = Number(match[2] ?? 0);
    if (hours > 23 || minutes > 59)
        return null;
    return hours * 60 + minutes;
}
/** 540 → `09:00`. Always two digits, both halves. */
function formatMinutes(minutes) {
    const whole = Math.floor(minutes / 60);
    const rest = minutes % 60;
    return `${String(whole).padStart(2, '0')}:${String(rest).padStart(2, '0')}`;
}
/**
 * Snap to the nearest quarter hour, clamped to the window.
 *
 * ⚠️ **15 minutes, because a drag is a gesture and not a measurement.** Without snapping, dragging
 * produces `09:07–10:23`, which nobody meant and which then has to be corrected in the dialog.
 */
function snapMinutes(minutes) {
    return Math.max(exports.CAL_FROM, Math.min(exports.CAL_TO, Math.round(minutes / 15) * 15));
}
/**
 * Place a day's entries: those with a start time where they say, the rest stacked from 09:00.
 *
 * ⚠️ **The stack is sequential, so two duration-only entries do not sit on top of each other.**
 * The cursor advances by each one's length — which is what makes an afternoon of three untimed
 * entries readable rather than a single opaque block.
 */
function placeDay(entries) {
    const timed = entries
        .filter((entry) => entry.startedAt !== null)
        .slice()
        .sort((a, b) => (a.startedAt ?? '').localeCompare(b.startedAt ?? ''));
    const untimed = entries.filter((entry) => entry.startedAt === null);
    const placed = [];
    for (const entry of timed) {
        const from = parseMinutes(entry.startedAt ?? '');
        /* ⚠️ An unparseable start time falls through to the stack rather than being dropped. The wire
         * schema makes this unreachable; dropping an entry the database holds would be worse than
         * placing it approximately. */
        if (from === null) {
            untimed.push(entry);
            continue;
        }
        placed.push({ entry, from, to: from + minutesOf(entry.hours) });
    }
    let cursor = exports.CAL_STACK_FROM;
    for (const entry of untimed) {
        const from = cursor;
        const to = from + minutesOf(entry.hours);
        cursor = to;
        placed.push({ entry, from, to });
    }
    placed.sort((a, b) => a.from - b.from || a.to - b.to);
    return packLanes(placed);
}
function minutesOf(hours) {
    return Math.round(Number(hours) * 60);
}
/**
 * Spread overlapping entries across side-by-side lanes.
 *
 * ⚠️⚠️ **A LANE IS REUSED AS SOON AS IT IS FREE, WHICH IS WHY THIS IS NOT "one lane per entry".**
 * Three entries where the first two overlap and the third starts after both need **two** lanes, not
 * three — the third goes back in lane 0. The naive version widens every group to its entry count
 * and a busy day becomes a row of slivers.
 *
 * ⚠️⚠️ **AND THE GROUP IS A TRANSITIVE CHAIN, not a pairwise overlap.** A overlaps B, B overlaps C,
 * A and C do not touch — all three are still one group, because B has to be beside both. Grouping
 * pairwise puts A and C in lane 0 and draws them at half width for no reason. The group ends only
 * when an entry starts at or after the **furthest end seen so far**, which is what `groupEnd`
 * tracks.
 *
 * Both are the mockup's `calLanes`, restated: it is correct and the reasoning is nowhere in it.
 */
function packLanes(items) {
    const out = [];
    let group = [];
    let groupEnd = -1;
    const flush = () => {
        /* One slot per lane, holding the end of whatever is currently in it. */
        const lanes = [];
        const assigned = group.map((item) => {
            let lane = lanes.findIndex((end) => end <= item.from);
            if (lane < 0) {
                lane = lanes.length;
                lanes.push(0);
            }
            lanes[lane] = item.to;
            return { item, lane };
        });
        for (const { item, lane } of assigned) {
            out.push({ entry: item.entry, from: item.from, to: item.to, lane, lanes: lanes.length });
        }
        group = [];
    };
    for (const item of items) {
        if (group.length > 0 && item.from >= groupEnd)
            flush();
        group.push(item);
        groupEnd = group.length === 1 ? item.to : Math.max(groupEnd, item.to);
    }
    if (group.length > 0)
        flush();
    return out;
}
/** Minutes from midnight → pixels from the top of the column. */
function minutesToPx(minutes) {
    return ((minutes - exports.CAL_FROM) / 60) * exports.CAL_ROW;
}
/** The column's full height in pixels. */
function columnHeightPx() {
    return ((exports.CAL_TO - exports.CAL_FROM) / 60) * exports.CAL_ROW;
}
//# sourceMappingURL=calendar.js.map